import CloseIcon from "@mui/icons-material/Close";
import { Modal } from "@mui/material";
import classNames from "classnames";
import { isObjectEmpty } from "helpers/utils";
import { t } from "i18next";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef } from "react";

import ExpandedHelpdeskIcon from "../ExpandedHelpdeskIcon";
import InfoTooltip from "../InfoTooltip";
import styles from "./style.module.scss";

interface IModalBase {
    /**
     * Value passed to open modal.
     */
    open: boolean;
    /**
     * Function that closes modal.
     */
    onClose: () => void;
    modalTitle: string | JSX.Element;
    modalSubtitle?: string;
    children: JSX.Element;
    /**
     * Style passed to content div
     */
    contentStyle?: React.CSSProperties;
    /**
     * If included, adds icon next to title to direct towards helpdesk.
     */
    infoTooltip?: { text: string; link: string };
    /**
     * To change colour of the background (negative space)
     */
    backgroundColor?: string;
    /**
     * Data needed to display expanded helpdesk icon.
     */
    helpdeskInfo?: { text: string; link: string };
    style?: CSSProperties;
    widthTransition?: { active: boolean; width: string };
    maxHeight?: string;
    titleStyle?: CSSProperties;
}

/**
 * For any modal needed to display a small form > like asking just the name to create a typology.
 */
const ModalBase = ({
    open,
    onClose,
    modalTitle,
    modalSubtitle,
    children,
    contentStyle,
    infoTooltip,
    backgroundColor = "",
    helpdeskInfo,
    style,
    widthTransition,
    maxHeight,
    titleStyle,
}: IModalBase) => {
    const newWidth = { "--personalised-width": widthTransition?.width };

    return (
        <Modal open={open} onClose={onClose} style={{ ...style, backgroundColor: backgroundColor }}>
            <div
                className={classNames({
                    [styles["modal-container"]]: true,
                    [styles["transition"]]: widthTransition?.active,
                })}
                style={{ ...style, ...newWidth, maxHeight }}
            >
                <div className={styles["modal-header"]}>
                    <div className={styles["modal-title"]} style={titleStyle}>
                        <h1>{modalTitle}</h1>
                        {infoTooltip && (
                            <InfoTooltip text={infoTooltip.text} link={infoTooltip.link} iconType="question" />
                        )}
                        {helpdeskInfo && <ExpandedHelpdeskIcon link={helpdeskInfo?.link} text={helpdeskInfo?.text} />}
                    </div>
                    <p>{modalSubtitle}</p>
                    <button
                        type="button"
                        className={styles["close-icon"]}
                        onClick={onClose}
                        aria-label={t("Common.Close")}
                    >
                        <CloseIcon />
                    </button>
                </div>
                <div className={styles["modal-content"]} style={contentStyle}>
                    {children}
                </div>
            </div>
        </Modal>
    );
};

export default ModalBase;
